import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import Responsive from 'react-responsive';
import 'react-table/react-table.css';
import styled, { css } from 'styled-components';
import classnames from 'classnames';
import theme from 'theme/theme';
import { ChevronDownIcon } from '../../Icon/index';
import DataTableSkeleton from './DataTableSkeleton';

const Th = styled.div`
  display: flex;
  justify-content: space-between;

  svg.sort-icon {
    margin-top: -3px;
    fill: ${theme.colors.medium};
    transition: all 0.2s;
    @media (max-width: ${theme.breakpoints.tablet}) {
      opacity: 1;
    }
  }

  &.-sort-asc,
  &.-sort-desc {
    box-shadow: none !important;
  }

  &.-sort-asc svg.sort-icon,
  &.-sort-desc svg.sort-icon {
    fill: ${theme.colors.primary};
    transform: rotate(90deg);
  }
  &.-sort-desc svg.sort-icon {
    transform: rotate(-90deg);
  }
`;

const ThComponent = ({ toggleSort, className, children, ...other }) => (
  // eslint-disable-next-line
  <Th
    className={classnames('rt-th', className)}
    onClick={e => toggleSort && toggleSort(e)}
    role="columnheader"
    tabIndex="-1"
    defaultFilterMethod={(filter, row) =>
      row[filter.id] !== undefined ? String(row[filter.id]).toLowerCase().includes(filter.value.toLowerCase()) : false
    }
    {...other}
  >
    {children}
    <ChevronDownIcon className="sort-icon" size="25px" />
  </Th>
);
ThComponent.propTypes = {
  toggleSort: PropTypes.func,
  className: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};
ThComponent.defaultProps = {
  children: [],
  toggleSort: () => {},
};

class DataTable extends React.Component {
  // eslint-disable-line

  render() {
    const { data, defaultPageSize, isMobile, columns, getTrGroupProps, skeleton, ...other } = this.props;
    const mobileColumnsVisible = columns.filter(col => col.showOnMobile);
    const mobileColumnsHidden = columns.filter(col => !col.showOnMobile);

    // eslint-disable-next-line
    const Skeleton = this.props.skeleton;
    const LoadingComponent = ({ loading }) => (loading ? <Skeleton /> : <></>);

    return (
      <div>
        <Responsive minWidth={theme.breakpoints.desktopLarge}>
          <ReactTable
            data={data}
            columns={columns}
            ThComponent={ThComponent}
            showPagination={false}
            pageSize={data.length}
            LoadingComponent={LoadingComponent}
            {...other}
          />
        </Responsive>
        <Responsive maxWidth={theme.breakpoints.desktopLarge}>
          <ReactTable
            data={data}
            columns={mobileColumnsVisible}
            ThComponent={ThComponent}
            LoadingComponent={LoadingComponent}
            // eslint-disable-next-line
            SubComponent={row => {
              return (
                <div className="subComponent">
                  <ReactTable
                    data={[data[row.index]]}
                    columns={mobileColumnsHidden}
                    showPagination={false}
                    minRows={1}
                  />
                </div>
              );
            }}
            showPagination={false}
            pageSize={data.length}
            {...other}
            getTrGroupProps={(state, row, _, props) => {
              // This is a workaround for CU-860r1mdu1
              if (!row) return {};
              const newProps = getTrGroupProps(state, row, _, props);
              const isExpanded = row.index in state.expanded && state.expanded[row.index];
              const defaultBackgroundColor =
                newProps && newProps.style && newProps.style.backgroundColor
                  ? newProps.style.backgroundColor
                  : 'inherit';
              const defaultClassName = newProps && newProps.className ? newProps.className : '';
              return {
                ...newProps,
                className: isExpanded ? `${defaultClassName} is-expanded` : defaultClassName,
                style: {
                  ...(newProps && newProps.style ? newProps.style : {}),
                  backgroundColor: isExpanded ? theme.colors.light : defaultBackgroundColor,
                },
              };
            }}
          />
        </Responsive>
      </div>
    );
  }
}

DataTable.propTypes = {
  data: PropTypes.any, // eslint-disable-line
  isMobile: PropTypes.any, // eslint-disable-line
  columns: PropTypes.any, // eslint-disable-line
  defaultPageSize: PropTypes.number,
  getTrGroupProps: PropTypes.func,
  skeleton: PropTypes.node,
};
DataTable.defaultProps = {
  defaultPageSize: 50,
  getTrGroupProps: () => {},
  skeleton: DataTableSkeleton,
};

export default styled(DataTable)`
  &.ReactTable {
    background: ${theme.colors.white};
    border: 0;
  }

  &.ReactTable > .rt-table {
    background: #fff;
  }

  &.ReactTable {
    margin-bottom: 140px;
  }

  &.ReactTable .rt-expandable {
    z-index: 10 !important;
    border: 0 !important;
    min-height: 47px !important;
    @media (max-width: ${theme.breakpoints.desktopLarge}) {
      min-height: 30px !important;
      position: absolute;
      right: 15px;
    }
  }

  &.ReactTable .subelements {
    margin: 20px -20px -10px -35px;
  }

  &.ReactTable .rt-thead.-header {
    box-shadow: none;
  }

  &.ReactTable .rt-thead,
  &.ReactTable .rt-tr-group {
    padding: 7px 35px;
  }

  &.ReactTable .rt-thead {
    padding: 5px 35px 4px 35px;
    background: ${theme.colors.light};
    border-bottom: 1px solid ${theme.colors.light};
    color: ${theme.colors.medium};
    font-size: ${theme.fontSizes.small};
  }

  &.ReactTable .rt-tr-group {
    position: relative;
  }

  &.ReactTable .subComponent .rt-thead {
    display: none;
  }

  &.ReactTable .subComponent .rt-td {
    width: auto !important;
  }

  &.ReactTable .subComponent .rt-tr-group {
    padding: 0 !important;
  }

  &.ReactTable .subComponent {
    margin: 10px -35px -10px -35px;
  }

  &.ReactTable .subComponent .ReactTable {
    background: ${theme.colors.white};
  }

  &.ReactTable .rt-tr {
    flex-wrap: wrap;
  }

  &.ReactTable .subComponent .rt-tbody {
    min-width: 100% !important;
    max-width: 100% !important;
  }

  &.ReactTable .rt-thead .rt-th,
  &.ReactTable .rt-tbody .rt-td {
    display: flex;
    align-items: center;
    text-align: left;
    padding: 5px 15px;
    min-height: 5px;
    border: 0;
    white-space: normal;
    &:focus {
      outline: none;
    }
  }

  &.ReactTable .rt-thead .rt-th {
    border-color: transparent;
    padding: 5px 15px 0 15px;
  }

  &.ReactTable .rt-tbody .rt-tr-group {
    border-color: ${theme.colors.light};
  }

  &.ReactTable .rt-noData {
    display: flex;
    position: relative;
    left: 0;
    top: 0;
    transform: none;
  }

  &.ReactTable .rt-thead .rt-resizable-header-content {
    overflow: visible;
  }

  &.ReactTable .rt-thead.-filters {
    height: 43px;
    overflow: hidden;
  }

  ${props =>
    props.hideFilterRow &&
    css`
      &.ReactTable .rt-thead.-filters {
        display: none;
      }
    `}

  &.checkbox-table .rt-table .rt-tr .rt-th:first-child {
    padding: 0 0 0 15px;
    svg.sort-icon {
      display: none;
    }
  }

  &.ReactTable .rt-thead .rt-th:not(.-cursor-pointer) .sort-icon {
    display: none;
  }

  @media (max-width: ${theme.breakpoints.desktopLarge}) {
    .rt-bulk-checkbox {
      flex: 40 0 auto !important;
      width: 40px !important;
      max-width: 40px !important;
    }
  }
`;
